class _UnityBridgeMock {
    constructor () {
        this._initialize();
    }

    _initialize () {
        window.vuplex = {
            // Public API
            postMessage: message => {
                console.debug('[_UnityBridgeMock.postMessage] Received message', message);
            },

            addEventListener: (event, callback) => {
                console.debug('[_UnityBridgeMock.addEventListener] Added event listener', event);
                window.addEventListener('message', callback);
            },

            // Private API
            mockSendMessage: (header, payload = {}) => {
                console.debug('[_UnityBridgeMock.mockSendMessage] Sending message', header, payload);
                const message = JSON.stringify({
                    header,
                    payload
                });

                window.dispatchEvent(new MessageEvent('message', {
                    data: message
                }));
            }
        };

        window.vuplex.mockSendMessage('initialized');

        console.debug('[_UnityBridgeMock._initialize] Initialized');
    }
}

class UnityBridge {
    constructor () {
        console.debug('[UnityBridge.constructor] Constructed');
        this._initialize();
    }

    _initialize () {
        try {
            this.listeners = {};

            this._startMockIfDevelopment();

            const initializedCallback = resolve => {
                resolve();
                this._setUpMessageListener();
                this.sendMessage('initialized');
            };

            this.initializePromise = new Promise(resolve => {
                if (window.vuplex) {
                    console.debug('[UnityBridge._initialize] Already initialized');
                    initializedCallback(resolve);
                } else {
                    console.debug('[UnityBridge._initialize] Waiting for the vuplexready event...');
                    window.addEventListener('vuplexready', () => {
                        console.debug('[UnityBridge._initialize] Initialized');
                        initializedCallback(resolve);
                    });
                }
            });
        } catch (e) {
            console.error('[UnityBridge.addListener] Error while initializing', e);
        }
    }

    _isDevelopment () {
        return process.env.NODE_ENV === 'development' && !navigator.userAgent.includes('KHTML');
    }

    _startMockIfDevelopment () {
        if (this._isDevelopment()) {
            console.debug('[UnityBridge._startMockIfDevelopment] Starting mock');
            this.mock = new _UnityBridgeMock();
        }
    }

    _parseMessage (event) {
        try {
            const json = event.data;
            const message = JSON.parse(json);
            let {header, payload} = message;

            // If payload is a string, parse it as JSON
            if (typeof payload === 'string') {
                try {
                    payload = JSON.parse(payload);
                } catch (e) {
                    // If parsing fails, keep it as string
                    console.debug('[UnityBridge._parseMessage] Payload is not JSON, keeping as string');
                }
            }

            return {header, payload};
        } catch (e) {
            if (!this._isDevelopment()) {
                console.warn('[UnityBridge._parseMessage] Error while parsing message', e);
            }
            return null;
        }
    }

    async _setUpMessageListener () {
        await this.initializePromise;

        window.vuplex.addEventListener('message', event => {
            try {
                const message = this._parseMessage(event);
                if (!message) {
                    return;
                }

                const {header, payload} = message;
                if (this.listeners[header]) {
                    console.debug('[UnityBridge._setUpMessageListener] Received message', header, payload);
          
                    this.listeners[header].forEach(callback => {
                        callback(payload);
                    });
                } else {
                    console.debug('[UnityBridge._setUpMessageListener] No listeners for message', header);
                }
            } catch (e) {
                console.error('[UnityBridge._setUpMessageListener] Error while setting up message listener', e);
            }
        });
    }

    addListener (header, callback) {
        try {
            if (!this.listeners[header]) {
                this.listeners[header] = [];
            }

            this.listeners[header].push(callback);
            console.debug('[UnityBridge.addListener] Added listener', header);
        } catch (e) {
            console.error('[UnityBridge.addListener] Error while adding listener', e);
        }
    }

    async sendMessage (header, payload = {}) {
        try {
            await this.initializePromise;

            // Convert payload to JSON string since Unity expects payload as string
            const payloadStr = JSON.stringify(payload);
            
            const message = JSON.stringify({
                header,
                payload: payloadStr
            });

            window.vuplex.postMessage(message);
            
            const truncatedPayload = payloadStr.length > 100 ?
                `${payloadStr.substring(0, 100)}...` :
                payloadStr;

            console.debug('[UnityBridge.sendMessage] Sent message', header, truncatedPayload);
        } catch (e) {
            console.error('[UnityBridge.sendMessage] Error while sending message', e);
        }
    }
}

const instance = new UnityBridge();
export default instance;
